import { useEffect, useState } from "react";
import { ApiError, api } from "../api.js";
import { nudgeJobActivity } from "./jobActivity.js";

export interface UploadInFlight {
  projectId: string;
  filename: string;
  /** 0 to 1, from XHR's own progress events. */
  progress: number;
  error: string | null;
  /**
   * The API's machine-readable reason, when it gave one.
   *
   * Carried so the project screen can tell a refusal apart from a failure. An
   * upload that was turned away for want of credit is not broken and does not
   * want a red box — it wants a way to add credit.
   */
  errorCode: string | null;
}

/**
 * The upload, outliving the screen that started it.
 *
 * Creating a project used to hold the form open until the last byte landed —
 * which for a screen recording is minutes of staring at a small bar below a
 * form you have finished filling in, with no sign that anything is happening to
 * the project you just made. The upload belongs to the project, not to the
 * page, so it lives here instead: the form starts it and navigates away
 * immediately, and the project's own screen picks it up and shows it as the
 * first step of the pipeline, which is what it is.
 *
 * A module singleton rather than context, for the same reason `jobActivity` is
 * one: the thing being tracked is global, and only one can be in flight from a
 * given tab. It does not survive a page reload — the File is gone by then, and
 * so is the request. A reload mid-upload cancels it, which is the truth and is
 * why the project screen says so rather than pretending to still be working.
 */
let current: UploadInFlight | null = null;
const subscribers = new Set<(value: UploadInFlight | null) => void>();

function publish(next: UploadInFlight | null): void {
  current = next;
  for (const notify of subscribers) notify(current);
}

/**
 * Sends the file, and returns at once.
 *
 * Deliberately not awaited by its caller. The returned promise settles when the
 * upload does, for anything that wants to know; ignoring it is the normal case.
 */
export function startUpload(projectId: string, file: File, narrate: boolean): Promise<void> {
  publish({ projectId, filename: file.name, progress: 0, error: null, errorCode: null });

  return api
    .upload(
      projectId,
      file,
      (fraction) => {
        // Guard against a second upload having started: a late progress event
        // from an abandoned one must not overwrite the current reading.
        if (current?.projectId === projectId) {
          publish({ ...current, progress: fraction });
        }
      },
      narrate,
    )
    .then(() => {
      // The processing job exists the moment the upload lands, so ask the job
      // poller now rather than letting the screen look idle for a tick.
      nudgeJobActivity();
      if (current?.projectId === projectId) publish(null);
    })
    .catch((cause: Error) => {
      if (current?.projectId === projectId) {
        publish({
          ...current,
          error: cause.message,
          errorCode: cause instanceof ApiError ? cause.code : null,
        });
      }
      // Swallowed on purpose: the error is on the store for the project screen
      // to render, and an unhandled rejection from a fire-and-forget call would
      // only reach the console.
    });
}

/** Clears a failed upload once its message has been seen. */
export function dismissUpload(): void {
  if (current?.error) publish(null);
}

/** The upload in flight for `projectId`, or null. */
export function useUploadInFlight(projectId?: string): UploadInFlight | null {
  const [value, setValue] = useState<UploadInFlight | null>(current);
  useEffect(() => {
    subscribers.add(setValue);
    setValue(current);
    return () => {
      subscribers.delete(setValue);
    };
  }, []);
  if (!value) return null;
  if (projectId && value.projectId !== projectId) return null;
  return value;
}
